/**
 * Author: Romain Hapi Sr.
 * Version: 2.0
 * Date: 2026-09-10
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { SHELL } from '../theme';
import Wordmark, { lockupHeightEm } from '../../components/Wordmark';
import { buildLegal } from '../../lib/legal';

/**
 * Les liens sociaux ont ete retires : ils pointaient tous les trois vers `#`.
 * Un lien mort en pied de page se remarque et se lit comme un site laisse en
 * plan. Ils reviendront le jour ou les comptes existeront — il suffira de
 * remplir SOCIAL_LINKS ci-dessous.
 */
const SOCIAL_LINKS = [];

const OFFICES = [
  {
    key: 'chad',
    address: "694 Avenue de Bezo, N'Djamena",
    phones: [
      { label: '+235 85 20 67 61', href: 'tel:+23585206761' },
      { label: '+235 90 17 66 13', href: 'tel:+23590176613' },
    ],
  },
  {
    key: 'uk',
    address: '30 Button Hill, Leeds, LS7 3DA',
    phones: [{ label: '+44 113 880 5181', href: 'tel:+441138805181' }],
  },
];

const SERVICE_LINKS = [
  { slug: 'digital-transformation', key: 'digitalTransformation' },
  { slug: 'consulting', key: 'consulting' },
  { slug: 'devops', key: 'devops' },
  { slug: 'development', key: 'development' },
];

export const Footer = () => {
  const { t, language } = useLanguage();
  const currentYear = new Date().getFullYear();

  const linkClass =
    'text-sm text-slate-400 transition-colors hover:text-amber-400';

  return (
    <footer data-testid="tchad-footer" className="relative overflow-hidden bg-slate-950 text-white">
      <div className="absolute inset-0 bg-tchad-weave" />
      {/* Filet or en tete de pied de page : la meme signature qu'en tete de
          section, qui referme la page au lieu de la laisser tomber. */}
      <div className="absolute inset-x-0 top-0 h-0.5 bg-amber-500" />

      <div className={`relative ${SHELL} py-16`}>
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            {/* La grille passe de deux a quatre colonnes a `lg`, ou la
                case retombe a environ 200 px : le logotype doit y redescendre,
                puis peut regrossir a `xl`. */}
            <div className="flex items-center gap-3 text-[2.25rem] lg:text-[1.9rem] xl:text-[2.25rem]">
              <img
                src="/images/logo-prenim-tchad.webp"
                alt=""
                aria-hidden="true"
                width="44"
                height="40"
                style={{ height: `${lockupHeightEm(0.4)}em` }}
                className="w-auto shrink-0 object-contain"
              />
              <Wordmark
                name="Prenim Tchad"
                tagline="Transformation Digitale"
                ratio={0.4}
                testId="tchad-footer-wordmark"
                nameClassName="text-white"
                taglineClassName="text-amber-400"
              />
            </div>
            <p className="mt-5 text-sm leading-relaxed text-slate-400">{t.hero.description}</p>

            {SOCIAL_LINKS.length ? (
              <div className="mt-6 flex gap-3">
                {SOCIAL_LINKS.map(({ href, label, Icon }) => (
                  <a
                    key={label}
                    href={href}
                    aria-label={label}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="flex h-10 w-10 items-center justify-center rounded-md border border-white/10 text-slate-300 transition-colors hover:border-amber-400/50 hover:text-amber-400"
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                ))}
              </div>
            ) : null}
          </div>

          <div>
            <h2 className="text-xs font-semibold uppercase tracking-widest text-amber-400">
              {t.footer.company}
            </h2>
            <ul className="mt-5 space-y-3">
              <li>
                <Link to="/tchad/about" className={linkClass}>
                  {t.nav.about}
                </Link>
              </li>
              <li>
                <Link to="/tchad/projects" className={linkClass}>
                  {t.nav.projects}
                </Link>
              </li>
              <li>
                <Link to="/tchad/careers" className={linkClass}>
                  {t.nav.careers}
                </Link>
              </li>
              <li>
                <Link to="/tchad/contact" className={linkClass}>
                  {t.nav.contact}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xs font-semibold uppercase tracking-widest text-amber-400">
              {t.footer.services}
            </h2>
            <ul className="mt-5 space-y-3">
              {SERVICE_LINKS.map(({ slug, key }) => (
                <li key={slug}>
                  <Link to={`/tchad/services/${slug}`} className={linkClass}>
                    {t.services[key].name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="text-xs font-semibold uppercase tracking-widest text-amber-400">
              {t.footer.contact}
            </h2>
            <div className="mt-5 space-y-6">
              {OFFICES.map((office) => (
                <div key={office.key}>
                  <p className="no-justify text-sm font-semibold text-white">
                    {t.contact[office.key]}
                  </p>
                  <p className="no-justify mt-2 flex items-start gap-2 text-sm text-slate-400">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{office.address}</span>
                  </p>
                  {office.phones.map((phone) => (
                    <p key={phone.href} className="mt-1 flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 shrink-0 text-slate-400" />
                      <a href={phone.href} className={linkClass}>
                        {phone.label}
                      </a>
                    </p>
                  ))}
                </div>
              ))}

              <p className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 shrink-0 text-slate-400" />
                <a href="mailto:hello@prenim.co.uk" className={linkClass}>
                  hello@prenim.co.uk
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Barre de bas de page alignee sur celle du site UK : la mention
            de droits a gauche, les liens juridiques a droite. Elle se replie au
            centre sur mobile, ou deux blocs cote a cote seraient a l'etroit. */}
        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-sm text-slate-500 md:flex-row">
          <p className="no-justify text-center md:text-start">
            © {currentYear} Prenim Tchad. {t.footer.rights}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <Link
              to="/tchad/privacy"
              data-testid="tchad-footer-privacy"
              className="transition-colors hover:text-amber-400"
            >
              {buildLegal('privacy', 'tchad', language).shortTitle}
            </Link>
            <Link
              to="/tchad/terms"
              data-testid="tchad-footer-terms"
              className="transition-colors hover:text-amber-400"
            >
              {buildLegal('terms', 'tchad', language).shortTitle}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
